import { useState, useEffect } from "react";
import { Button, Grid, styled, CircularProgress, IconButton } from "@mui/material";
import { Delete } from "@mui/icons-material";
import AppModal from "components/AppModal";
import FlexBox from "components/flexbox/FlexBox";
import AppTextField from "components/input-fields/AppTextField";
import { H2, H6 } from "components/Typography";
import { useFormik } from "formik";
import toast from "react-hot-toast";
import * as Yup from "yup";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { API_ENDPOINTS } from "Network/apiEndpoints";
import apiService from "Network/apiService";
import CityDropdown from "page-sections/Members/CityDropdown";
import AddPhotoAlternateIcon from '@mui/icons-material/AddPhotoAlternate'; // Import the AddPhotoAlternate icon

const StyledAppModal = styled(AppModal)(({ theme }) => ({
    maxWidth: '34vw', // Adjust as needed, use percentage for responsiveness
    minWidth: 300,
    maxHeight: '80vh', // Add a maximum height to the modal
    overflowY: 'auto', // Enable vertical scrolling if content overflows
    [theme.breakpoints.down(600)]: { // Adjust breakpoint as needed
        maxWidth: "100vw",
        margin: 0,
        borderRadius: 0,
    },
    padding: theme.spacing(2), // Add padding for better spacing
}));

const ImagePreviewContainer = styled('div')({
    display: 'flex',
    flexWrap: 'wrap',
    gap: '10px',
    marginTop: '10px'
});

const ImagePreview = styled('div')({
    position: 'relative',
    width: '100px',
    height: 'auto', // Modified this line
    '& img': {
        width: '100%',
        height: '100%',
        objectFit: 'cover',
        borderRadius: '4px'
    }
});

const DeleteButton = styled(IconButton)(({ theme }) => ({
    position: 'absolute',
    top: -8,
    right: -8,
    backgroundColor: 'rgba(0, 0, 0, 0.7)', // Dark Background
    color: 'white', // White Icon Color
    padding: '4px',
    '&:hover': {
        backgroundColor: 'rgba(0, 0, 0, 0.9)' // Darker on Hover
    }
}));

const ImageInput = styled('input')({
    display: 'none'
});

const AddEventModal = ({ open, onClose, edit, data }) => {
    const [loading, setLoading] = useState(false);
    const [selectedImages, setSelectedImages] = useState([]);
    const [imageFiles, setImageFiles] = useState([]);

    const validationSchema = Yup.object().shape({
        title: Yup.string().required("Event Title is Required"),
        description: Yup.string().required("Event Description is Required"),
        startDate: Yup.date()
            .required("Start Date is Required")
            .min(new Date(new Date().setHours(0, 0, 0, 0) + 24 * 60 * 60 * 1000), "Start Date must be a future date"),
        endDate: Yup.date()
            .required("End Date is Required")
            .min(Yup.ref("startDate"), "End Date must be greater than or equal to Start Date"),
        city: Yup.string().required("City is Required"),
        address: Yup.string().required("Address is Required"),
        mapLocation: Yup.string().required("Google Map Location is Required"),
    });

    const handleImageChange = (e) => {
        const files = Array.from(e.target.files);

        // Limit the number of images to 3
        if (selectedImages.length + files.length > 3) {
            toast.error("You can only upload a maximum of 3 images.");
            return;
        }
        
        // Validate file types and sizes
        const validFiles = files.filter(file => {
            const isValid = file.type.startsWith('image/');
            const isValidSize = file.size <= 5 * 1024 * 1024; // 5MB limit
            if (!isValid) toast.error(`${file.name} is not an image file`);
            if (!isValidSize) toast.error(`${file.name} is too large (max 5MB)`);
            return isValid && isValidSize;
        });

        if (validFiles.length > 0) {
            // Create preview URLs
            const newImagePreviews = validFiles.map(file => URL.createObjectURL(file));
            setSelectedImages(prev => [...prev, ...newImagePreviews]);
            setImageFiles(prev => [...prev, ...validFiles]);
        }
    };

    const handleRemoveImage = (index) => {
        setSelectedImages(prev => prev.filter((_, i) => i !== index));
        setImageFiles(prev => prev.filter((_, i) => i !== index));
    };

    const { values, errors, touched, handleChange, handleSubmit, setFieldValue } = useFormik({
        initialValues: {
            title: data?.title || "",
            description: data?.description || "",
            startDate: data?.startDate || null,
            endDate: data?.endDate || null,
            city: data?.city || "",
            address: data?.address || "",
            mapLocation: data?.mapLocation || "",
        },
        validationSchema,
        onSubmit: async (values) => {
            const formatISODate = (date) => {
                if (!date) return null;
            
                // Extract year, month, and day explicitly
                const year = date.getFullYear();
                const month = String(date.getMonth() + 1).padStart(2, '0'); // Months are zero-based
                const day = String(date.getDate()).padStart(2, '0');
            
                // Return in YYYY-MM-DD format
                return `${year}-${month}-${day}`;
            };
            

            setLoading(true);

            try {
                const formData = new FormData();
                formData.append('user', JSON.parse(localStorage.getItem("logged_data") || "{}")?.user?.id);
                formData.append('title', values.title);
                formData.append('description', values.description);
                formData.append('start_day', formatISODate(values.startDate));
                formData.append('end_day', formatISODate(values.endDate));
                formData.append('city', values.city);
                formData.append('address', values.address);
                formData.append('map_location', values.mapLocation);

                imageFiles.forEach((file) => {
                    formData.append(`images`, file);
                });

                let response;
                if (edit) {
                    response = await apiService.put(API_ENDPOINTS.PUT_update_event(data.id), formData);
                } else {
                    response = await apiService.post(API_ENDPOINTS.POST_create_event, formData, "token", "maltipart/form");
                }

                if (response?.success) {
                    toast.success(`Event ${edit ? "Updated" : "Added"} Successfully`);
                    onClose(true);
                } else {
                    toast.error(response?.message || `Failed to ${edit ? "update" : "add"} event`);
                }
            } catch (error) {
                console.error("API error:", error);
                toast.error("An unexpected error occurred");
            } finally {
                setLoading(false);
            }
        }
    });

    // Reset form and selected images when modal closes
    useEffect(() => {
        if (!open) {
            setSelectedImages([]);
            setImageFiles([]);
        }
    }, [open]);

    return (
        <StyledAppModal open={open} handleClose={() => onClose(false)}>
            <H2 mb={2}>{edit ? "Edit Event" : "Add New Event"}</H2>

            <form onSubmit={handleSubmit}>
                <Grid container spacing={3}>
                    {/* Existing form fields remain the same */}
                    <Grid item xs={12}>
                        <H6 mb={1}>Event Title</H6>
                        <AppTextField
                            fullWidth
                            size="small"
                            name="title"
                            placeholder="Enter Event Title"
                            value={values.title}
                            onChange={handleChange}
                            error={Boolean(errors.title && touched.title)}
                            helperText={touched.title && errors.title}
                        />
                    </Grid>

                    <Grid item xs={12}>
                        <H6 mb={1}>Event Description</H6>
                        <AppTextField
                            fullWidth
                            multiline
                            rows={4}
                            size="small"
                            name="description"
                            placeholder="Enter Event Description"
                            value={values.description}
                            onChange={handleChange}
                            error={Boolean(errors.description && touched.description)}
                            helperText={touched.description && errors.description}
                        />
                    </Grid>

                    <Grid item xs={12} md={6}>
                        <H6 mb={1}>Start Date</H6>
                        <LocalizationProvider dateAdapter={AdapterDateFns}>
                            <DatePicker
                                value={values.startDate}
                                onChange={(date) => setFieldValue("startDate", date)}
                                renderInput={(params) => (
                                    <AppTextField
                                        {...params}
                                        fullWidth
                                        size="small"
                                        error={Boolean(errors.startDate && touched.startDate)}
                                        helperText={touched.startDate && errors.startDate}
                                    />
                                )}
                                minDate={new Date(new Date().setHours(0, 0, 0, 0) + 24 * 60 * 60 * 1000)}
                            />
                        </LocalizationProvider>
                    </Grid>

                    <Grid item xs={12} md={6}>
                        <H6 mb={1}>End Date</H6>
                        <LocalizationProvider dateAdapter={AdapterDateFns}>
                            <DatePicker
                                value={values.endDate}
                                onChange={(date) => setFieldValue("endDate", date)}
                                minDate={values.startDate}
                                renderInput={(params) => (
                                    <AppTextField
                                        {...params}
                                        fullWidth
                                        size="small"
                                        error={Boolean(errors.endDate && touched.endDate)}
                                        helperText={touched.endDate && errors.endDate}
                                    />
                                )}
                            />
                        </LocalizationProvider>
                    </Grid>

                    <Grid item xs={12} md={6}>
                        <H6 mb={1}>City</H6>
                        <CityDropdown
                            name="city"
                            label="City*"
                            value={values.city}
                            onChange={(value) => setFieldValue("city", value)}
                            error={Boolean(errors.city && touched.city)}
                            helperText={errors.city}
                        />
                    </Grid>

                    <Grid item xs={12}>
                        <H6 mb={1}>Full Address</H6>
                        <AppTextField
                            fullWidth
                            multiline
                            rows={2}
                            size="small"
                            name="address"
                            placeholder="Enter Full Address"
                            value={values.address}
                            onChange={handleChange}
                            error={Boolean(errors.address && touched.address)}
                            helperText={touched.address && errors.address}
                        />
                    </Grid>

                    <Grid item xs={12}>
                        <H6 mb={1}>Google Map Location</H6>
                        <AppTextField
                            fullWidth
                            size="small"
                            name="mapLocation"
                            placeholder="Enter Google Map Location URL"
                            value={values.mapLocation}
                            onChange={handleChange}
                            error={Boolean(errors.mapLocation && touched.mapLocation)}
                            helperText={touched.mapLocation && errors.mapLocation}
                        />
                    </Grid>

                    {/* New Image Upload Section */}
                    <Grid item xs={12}>
                        <H6 mb={1}>Event Images</H6>
                        <label htmlFor="event-images">
                            <Button
                                variant="outlined"
                                component="span"
                                startIcon={<AddPhotoAlternateIcon />} // Use the imported icon
                                disabled={selectedImages.length >= 3} // Disable the button when 3 images are selected
                            >
                                Upload Images
                            </Button>
                            <ImageInput
                                accept="image/*"
                                id="event-images"
                                multiple
                                type="file"
                                onChange={handleImageChange}
                                disabled={selectedImages.length >= 3} // Disable the input as well
                            />
                        </label>

                        <ImagePreviewContainer>
                            {selectedImages.map((preview, index) => (
                                <ImagePreview key={index}>
                                    <img src={preview} alt={`Preview ${index + 1}`} />
                                    <DeleteButton
                                        size="small"
                                        onClick={() => handleRemoveImage(index)}
                                    >
                                        <Delete fontSize="small" />
                                    </DeleteButton>
                                </ImagePreview>
                            ))}
                        </ImagePreviewContainer>
                    </Grid>
                </Grid>

                <FlexBox justifyContent="flex-end" gap={2} marginTop={4}>
                    <Button fullWidth size="small" variant="outlined" onClick={() => onClose(false)}>
                        Cancel
                    </Button>
                    <Button
                        fullWidth
                        size="small"
                        type="submit"
                        variant="contained"
                        disabled={loading}
                    >
                        {loading ? <CircularProgress size={24} color="inherit" /> : edit ? "Update Event" : "Add Event"}
                    </Button>
                </FlexBox>
            </form>
        </StyledAppModal>
    );
};

export default AddEventModal;
